import { Dispatch, SetStateAction, useCallback, useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
    Box,
    CircularProgress,
    InputAdornment,
    Paper,
    Stack,
    TextField,
    Tooltip,
    Typography,
    useTheme,
} from '@mui/material';
import { CODE_ENTER } from 'keycode-js';
import { z } from 'zod';

import ChatIconUrl from '@icons/chat-icon.svg?url';
import Button from '@components/Button';
import ErrorMessage from '@components/ErrorMessage';
import { useChat } from '@hooks/useChat';
import { useOnClickOutside } from '@hooks/useOnClickOutside';
import { useInputStore } from '@store/inputStore';
import { useLocationStore } from '@store/locationStore';
import { useMessagesStore } from '@store/messagesStore';
import { SharedApiUrl } from '@services/endpoints';

import {
    improvePromptButtons,
    improvePromptTexts,
    messageInputButtons,
    messageInputTexts,
} from '../constants';
import { useImprovePrompt } from '../services/queries';

interface ImprovePromptProps {
    message: string;
    setMessage: (message: string) => void;
    setShowImprovePromptPopup: Dispatch<SetStateAction<boolean>>;
}

const ImprovePrompt = ({ message, setMessage, setShowImprovePromptPopup }: ImprovePromptProps) => {
    const theme = useTheme();
    const { mutate: improvePrompt, isPending, isError, error, data } = useImprovePrompt();

    const handleClickOutside = useCallback(() => {
        setShowImprovePromptPopup(false);
    }, [setShowImprovePromptPopup]);

    const popupRef = useOnClickOutside<HTMLDivElement>(handleClickOutside);

    useEffect(() => {
        improvePrompt({ promptToImprove: message });
    }, [message, improvePrompt]);

    return (
        <Paper
            ref={popupRef}
            sx={{
                position: 'absolute',
                bottom: 0,
                right: 0,
                zIndex: 1000,
                width: '661px',
                backgroundColor: theme.palette.customColors.lightGray,
                padding: theme.spacing(3),
                display: 'flex',
                flexDirection: 'column',
                gap: theme.spacing(3),
                boxShadow: theme.customShadows.secondary,
            }}
            square
        >
            {isPending && (
                <Stack justifyContent='center' alignItems='center'>
                    <CircularProgress sx={{ color: theme.palette.customColors.purple }} />
                </Stack>
            )}
            {isError && <ErrorMessage error={error} />}
            {data && (
                <>
                    <Typography
                        variant='h4'
                        color={theme.palette.customColors.black}
                        fontSize='1.25rem'
                        fontWeight={600}
                    >
                        {improvePromptTexts.header}
                    </Typography>
                    <Stack direction='row' gap={1} alignItems='flex-start'>
                        <img src={ChatIconUrl} alt='' aria-hidden='true' />
                        <Typography
                            variant='body2'
                            noWrap={false}
                            sx={{
                                whiteSpace: 'normal',
                                wordWrap: 'break-word',
                                overflowWrap: 'break-word',
                            }}
                        >
                            {data.improvedPrompt}
                        </Typography>
                    </Stack>
                    <Stack direction='row' justifyContent='flex-end' gap={1}>
                        <Button
                            colorVariant='orange'
                            iconType='cancel'
                            onClick={() => setShowImprovePromptPopup(false)}
                        >
                            {improvePromptButtons.cancel}
                        </Button>
                        <Button
                            iconType='reload'
                            onClick={() => improvePrompt({ promptToImprove: message })}
                        >
                            {improvePromptButtons.reload}
                        </Button>
                        <Button
                            colorVariant='primaryLinearGradient'
                            whiteText
                            iconType='send'
                            onClick={() => {
                                setMessage(data.improvedPrompt);
                                setShowImprovePromptPopup((prevState) => !prevState);
                            }}
                        >
                            {improvePromptButtons.addAsInput}
                        </Button>
                    </Stack>
                </>
            )}
        </Paper>
    );
};

interface IdleButtonsProps {
    isValid: boolean;
    resetForm: () => void;
    setShowImprovePromptPopup: Dispatch<SetStateAction<boolean>>;
}

const IdleButtons = ({ isValid, resetForm, setShowImprovePromptPopup }: IdleButtonsProps) => {
    const theme = useTheme();
    const messages = useMessagesStore((state) => state.messages);
    const resetChatHistory = useMessagesStore((state) => state.resetChatHistory);

    const hasMessages = messages.length > 0;
    const showClearChatAndInputButton = hasMessages || isValid;

    const handleClearChatAndInput = () => {
        resetChatHistory();
        resetForm();
    };

    return (
        <>
            {showClearChatAndInputButton && (
                <Tooltip
                    title={messageInputTexts.tooltip}
                    placement='top'
                    slotProps={{
                        tooltip: {
                            sx: {
                                fontSize: '1rem',
                                padding: theme.spacing(1.5),
                                backgroundColor: theme.palette.customColors.lightGray,
                                boxShadow: theme.customShadows.secondary,
                                color: theme.palette.customColors.black,
                            },
                        },
                    }}
                >
                    <Button
                        iconType='cancel'
                        variant='contained'
                        colorVariant='lightPurple'
                        onClick={handleClearChatAndInput}
                    >
                        {messageInputButtons.clearChatAndInput}
                    </Button>
                </Tooltip>
            )}
            {isValid && (
                <Button
                    iconType='improve'
                    variant='outlined'
                    colorVariant='secondaryLinearGradient'
                    onClick={() => {
                        setShowImprovePromptPopup((prevState) => !prevState);
                    }}
                >
                    {messageInputButtons.promptImprovement}
                </Button>
            )}
            <Button
                type='submit'
                iconType='send'
                variant='contained'
                colorVariant='primaryLinearGradient'
                whiteText
                disabled={!isValid}
            >
                {messageInputButtons.send}
            </Button>
        </>
    );
};

interface PendingButtonsProps {
    stopStreaming: () => void;
}

const PendingButtons = ({ stopStreaming }: PendingButtonsProps) => {
    const theme = useTheme();

    return (
        <Stack direction='row' spacing={1} alignItems='center'>
            <CircularProgress
                size={24}
                sx={{
                    color: theme.palette.customColors.purple,
                }}
            />
            <Button variant='outlined' colorVariant='orange' onClick={stopStreaming}>
                {messageInputButtons.stop}
            </Button>
        </Stack>
    );
};

const MESSAGE_FIELD = 'message' as const;

const messageSchema = z.object({
    [MESSAGE_FIELD]: z.string().trim().min(1),
});

const defaultMessage = '';

type MessageFormData = z.infer<typeof messageSchema>;

const MessageInput = () => {
    const locationItem = useLocationStore((state) => state.location);
    const [showImprovePromptPopup, setShowImprovePromptPopup] = useState<boolean>(false);
    const { sendMessage, isPending, stopStreaming } = useChat(SharedApiUrl.ChatGeneral);
    const theme = useTheme();
    const inputValue = useInputStore((state) => state.inputValue);
    const setInputValue = useInputStore((state) => state.setInputValue);

    const {
        control,
        handleSubmit,
        formState: { isValid },
        watch,
        setValue,
        reset,
    } = useForm<MessageFormData>({
        resolver: zodResolver(messageSchema),
        mode: 'onChange',
        defaultValues: {
            [MESSAGE_FIELD]: defaultMessage,
        },
    });

    useEffect(() => {
        if (inputValue) {
            setValue(MESSAGE_FIELD, inputValue, { shouldValidate: true });
        }
    }, [inputValue, setValue]);

    const messageValue = watch(MESSAGE_FIELD) ?? defaultMessage;

    const resetForm = () => {
        reset();
        setInputValue(defaultMessage);
    };

    const onSubmit = (data: MessageFormData) => {
        const message = data[MESSAGE_FIELD];
        sendMessage(message, locationItem?.value);
        resetForm();
    };

    const handleSubmitMessage = handleSubmit(onSubmit);

    return (
        <Box
            component='form'
            sx={{
                paddingLeft: 1,
                paddingRight: 1,
            }}
            onSubmit={handleSubmitMessage}
        >
            <Controller
                name={MESSAGE_FIELD}
                control={control}
                render={({ field }) => (
                    <TextField
                        {...field}
                        fullWidth
                        variant='outlined'
                        placeholder={messageInputTexts.placeholder}
                        autoComplete='off'
                        multiline
                        onKeyDown={(e) => {
                            if (e.key === CODE_ENTER && !e.shiftKey) {
                                e.preventDefault();
                                handleSubmitMessage();
                            }
                        }}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                borderTopRightRadius: theme.borderRadius.large,
                                borderBottomLeftRadius: theme.borderRadius.large,
                                backgroundColor: theme.palette.customColors.lightGray,
                                minHeight: '60px',
                                fontWeight: 400,
                                fontSize: '0.875rem',
                                '& input:-webkit-autofill': {
                                    WebkitBoxShadow: `0 0 0 1000px ${theme.palette.customColors.lightGray} inset`,
                                },
                                '& fieldset': {
                                    border: 'none',
                                },
                            },
                        }}
                        slotProps={{
                            input: {
                                startAdornment: (
                                    <InputAdornment position='start'>
                                        <img src={ChatIconUrl} alt='' aria-hidden='true' />
                                    </InputAdornment>
                                ),
                                endAdornment: (
                                    <InputAdornment position='end'>
                                        <Stack direction='row' gap={1} position='relative'>
                                            {isPending ? (
                                                <PendingButtons stopStreaming={stopStreaming} />
                                            ) : (
                                                <IdleButtons
                                                    isValid={isValid}
                                                    resetForm={resetForm}
                                                    setShowImprovePromptPopup={
                                                        setShowImprovePromptPopup
                                                    }
                                                />
                                            )}
                                            {showImprovePromptPopup && (
                                                <ImprovePrompt
                                                    message={messageValue}
                                                    setMessage={(text: string) =>
                                                        setValue(MESSAGE_FIELD, text)
                                                    }
                                                    setShowImprovePromptPopup={
                                                        setShowImprovePromptPopup
                                                    }
                                                />
                                            )}
                                        </Stack>
                                    </InputAdornment>
                                ),
                            },
                        }}
                    />
                )}
            />
        </Box>
    );
};

export default MessageInput;
